import { MNSproCloudApi } from '..';
import { paginationLoadRemainingPages } from '../../utils/paginated';
import {
  PaginatedOfPasswordsWorkgroupsResponse,
  PasswordsCoursesResponse,
  PasswordsUserResponse,
  PasswordsWorkgroupsResponse,
  ValidatePasswordResponse,
  ValidationParameters,
} from '../api/v2';
import {
  ChangePasswordRequest,
  ChangePasswordResult,
} from './PasswordsService.interface';

export class PasswordsService {
  private static getApi(authToken: string) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.PasswordsApiFactory(config);

    return api;
  }

  /**
   * Fetches all courses for the organization in the given time period.
   * @param authToken OAuth bearer token for the MNSpro Cloud API.
   * @param organizationId Target organization id.
   * @param timePeriod Time period id scoping the course list.
   * @returns Course summaries usable as password-change targets.
   */
  static async getCourses(
    authToken: string,
    organizationId: string,
    timePeriod: string,
  ): Promise<PasswordsCoursesResponse[]> {
    const api = PasswordsService.getApi(authToken);
    const response = await api.getAllBasicCourses(organizationId, timePeriod);

    return response.data.value;
  }

  /**
   * Fetches all users in the organization (UI filters to students).
   * @param authToken OAuth bearer token for the MNSpro Cloud API.
   * @param organizationId Target organization id.
   * @returns Full user list for the organization.
   */
  static async getUsers(
    authToken: string,
    organizationId: string,
  ): Promise<PasswordsUserResponse[]> {
    const api = PasswordsService.getApi(authToken);
    const response = await api.getAllUsers(organizationId);

    return response.data;
  }

  /**
   * Fetches all workgroups for the organization, automatically loading remaining
   * pages from the paginated endpoint.
   * @param authToken OAuth bearer token for the MNSpro Cloud API.
   * @param organizationId Target organization id.
   * @param timePeriod Time period id scoping the workgroup list.
   * @returns Aggregated workgroup list across all pages.
   */
  static async getWorkgroups(
    authToken: string,
    organizationId: string,
    timePeriod: string,
  ): Promise<PasswordsWorkgroupsResponse[]> {
    const api = PasswordsService.getApi(authToken);
    const initialResponse = await api.getAllWorkgroups(
      organizationId,
      timePeriod,
    );

    const records = paginationLoadRemainingPages<
      PasswordsWorkgroupsResponse,
      PaginatedOfPasswordsWorkgroupsResponse
    >(authToken, initialResponse);

    return records;
  }

  /**
   * Changes passwords for a list of users via sequential calls to
   * `PUT /api/v2/passwords/{organizationId}/changeUserPassword`. Per-user
   * outcomes are aggregated and returned even on partial failures.
   * @param authToken OAuth bearer token for the MNSpro Cloud API.
   * @param organizationId Target organization id.
   * @param changePasswordRequests One entry per user to update.
   * @returns Per-user result with state, password, and optional error message.
   */
  static async changePassword(
    authToken: string,
    organizationId: string,
    changePasswordRequests: ChangePasswordRequest[],
  ): Promise<ChangePasswordResult[]> {
    const api = PasswordsService.getApi(authToken);
    const results: ChangePasswordResult[] = [];

    for (const request of changePasswordRequests) {
      const response = await api.changeUserPassword(organizationId, {
        userId: request.userId,
        newPassword: request.newPassword,
        passwordIsPermanentValid: request.passwordIsPermanentValid,
      });

      results.push({
        id: request.userId,
        state: response.data.state,
        password: request.newPassword,
        displayName: request.displayName,
        errorMessage: response.data.errorMessage || null,
      });
    }

    return results;
  }

  /**
   * Validates a candidate password against the organization's Azure AD policies
   * (length, banned-password list, etc.).
   * @param authToken OAuth bearer token for the MNSpro Cloud API.
   * @param organizationId Target organization id.
   * @param password Password to validate.
   * @returns Validation result with per-rule pass/fail flags.
   */
  static async validatePassword(
    authToken: string,
    organizationId: string,
    password: string,
  ): Promise<ValidatePasswordResponse> {
    const api = PasswordsService.getApi(authToken);
    const validationParameters: ValidationParameters = {
      password,
    };

    const response = await api.validatePassword(
      organizationId,
      validationParameters,
    );
    return response.data;
  }
}
